// src/services/axiosConfig.js
import axios from 'axios';

// ✅ Use environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api/v1';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT) || 60000;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Track if we're currently waking up the backend
let isWakingUp = false;

// Request interceptor - Add access token to every request
axiosInstance.interceptors.request.use(
  (config) => {
    // ✅ Get token from sessionStorage (primary) or localStorage (backup)
    const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // ===== HANDLE NETWORK ERRORS (Backend Sleeping) =====
    if (!error.response && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Only show alert once while waking up
      if (!isWakingUp) {
        isWakingUp = true;
        console.log('⏰ Backend is sleeping, waking it up...');
        
        // Show user-friendly message
        const wakeUpMessage = document.createElement('div');
        wakeUpMessage.id = 'backend-wakeup-message';
        wakeUpMessage.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #1e40af;
          color: white;
          padding: 16px 24px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          z-index: 10000;
          font-family: system-ui, -apple-system, sans-serif;
          max-width: 400px;
          animation: slideIn 0.3s ease-out;
        `;
        wakeUpMessage.innerHTML = `
          <div style="display: flex; align-items: center; gap: 12px;">
            <div style="
              width: 24px;
              height: 24px;
              border: 3px solid rgba(255,255,255,0.3);
              border-top-color: white;
              border-radius: 50%;
              animation: spin 1s linear infinite;
            "></div>
            <div>
              <div style="font-weight: 600; margin-bottom: 4px;">Waking up backend...</div>
              <div style="font-size: 14px; opacity: 0.9;">This may take 30-60 seconds (free tier)</div>
            </div>
          </div>
        `;
        
        // Add animations
        const style = document.createElement('style');
        style.textContent = `
          @keyframes slideIn {
            from { transform: translateX(400px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `;
        document.head.appendChild(style);
        document.body.appendChild(wakeUpMessage);
      }
      
      // Wait and retry
      console.log('⏰ Retrying in 5 seconds...');
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      try {
        const response = await axiosInstance(originalRequest);
        
        // Success! Remove wake-up message
        isWakingUp = false;
        const message = document.getElementById('backend-wakeup-message');
        if (message) {
          message.style.animation = 'slideOut 0.3s ease-out';
          setTimeout(() => message.remove(), 300);
        }
        
        console.log('✅ Backend is awake!');
        return response;
      } catch (retryError) {
        // Still failing after retry
        isWakingUp = false;
        const message = document.getElementById('backend-wakeup-message');
        if (message) {
          message.style.background = '#dc2626';
          message.innerHTML = `
            <div>
              <div style="font-weight: 600; margin-bottom: 4px;">⚠️ Backend Unreachable</div>
              <div style="font-size: 14px;">Please try again in a moment</div>
            </div>
          `;
          setTimeout(() => message.remove(), 5000);
        }
        return Promise.reject(retryError);
      }
    }
    
    console.error('API ERROR:', error);
    
    // ===== HANDLE 401 - TOKEN EXPIRED, REDIRECT TO LOGIN =====
    if (error.response?.status === 401) {
      console.log('❌ Token expired or invalid - redirecting to login');
      
      // Clear all auth data
      clearAuthData();
      
      alert('Your session has expired. Please login again.');
      window.location.href = '/login';
      
      return Promise.reject(error);
    }
    
    // ===== HANDLE 403 - FORBIDDEN =====
    if (error.response?.status === 403) {
      console.warn('⚠️ Access forbidden');
      alert('Access forbidden. You do not have permission for this action.');
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

// Helper function to clear all auth data
const clearAuthData = () => {
  sessionStorage.removeItem('accessToken');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('tokenExpiry');
  localStorage.removeItem('user');
};

// Logout function - Clear local data and redirect
export const logout = async () => {
  try {
    console.log('🚪 Logging out...');
    
    // ✅ Try to call backend logout endpoint (optional, may fail if token expired)
    try {
      await axios.post(
        `${API_BASE_URL}/auth/logout`,
        {},
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      console.log('✅ Backend logout successful');
    } catch (err) {
      console.log('⚠️ Backend logout failed (token may be expired), continuing...');
    }
    
    // Clear all local auth data
    clearAuthData();
    
    // Redirect to login
    window.location.href = '/login';
    
    return { success: true };
  } catch (error) {
    console.error('❌ Logout error:', error);
    
    // Even if backend fails, clear local data and redirect
    clearAuthData();
    window.location.href = '/login';
    
    return { success: false, error };
  }
};

// Optional: Log configuration in development
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 Axios Config:', {
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    withCredentials: true,
  });
}

export default axiosInstance;